import { Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';
import FallbackUI from './fallback-ui';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  qualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
  },
  disqualifiedDiv: {
    padding: '11.5px 16px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
  },
  screeningItemDiv: {
    borderRadius: 6,
    border: `1px solid ${theme.palette.border.primaryGreen1}`,
    position: 'relative',
  },
  screeningItemDivError: {
    borderRadius: 6,
    border: `1px solid ${theme.palette.border.primaryRed1}`,
  },
  qualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 90,
    borderRadius: 22,
    background: theme.palette.content.primaryGreen2,
    color: theme.palette.background.primaryGreen1,
  },
  disqualifiedIndicatorDiv: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '6px 8px',
    marginTop: -10,
    width: 112,
    borderRadius: 22,
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
  },
  tickIcon: {
    color: theme.palette.background.primaryGreen1,
  },
  crossIcon: {
    color: theme.palette.background.primaryRed1,
  },
  remarkText: {
    lineHeight: 'normal',
    color: theme.palette.content.primaryBlack3,
  },
}));

const QualifiedTag = ({ success, forCriteria }) => {
  const classes = useStyles();

  return (
    <Stack
      className={
        success
          ? classes.qualifiedIndicatorDiv
          : classes.disqualifiedIndicatorDiv
      }
      sx={{
        position: 'absolute',
        right: '10px',
        top: {
          xs: '16px',
          md: '20px',
        },
      }}
    >
      {success ? (
        <Iconify
          width={16}
          height={16}
          className={classes.tickIcon}
          icon="tabler:circle-check-filled"
        />
      ) : (
        <Iconify
          width={16}
          height={16}
          className={classes.crossIcon}
          icon="tabler:circle-x-filled"
        />
      )}
      {!forCriteria && (
        <Typography variant="caption" sx={{ fontSize: '12px' }}>
          {success ? 'Qualified' : 'Disqualified'}
        </Typography>
      )}
      {forCriteria && (
        <Typography variant="caption" sx={{ fontSize: '12px' }}>
          {success ? 'Matching' : 'Not Matching'}
        </Typography>
      )}
    </Stack>
  );
};

const ScreeningGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;
  const mdUp = useResponsive('up', 'md');

  // variables
  const skillMatch = candidateDetails?.skill_match;
  const criteriaMatch = candidateDetails?.criteria_match;

  return (
    <Stack
      sx={{
        height: '100%',
        gap: {
          xs: '20px',
          md: '37px',
        },
      }}
    >
      {skillMatch ? (
        <>
          <Stack
            className={
              skillMatch?.selected === true
                ? classes.qualifiedDiv
                : classes.disqualifiedDiv
            }
          >
            <Typography variant="subheading1">
              {skillMatch?.selected === true ? 'Qualified' : 'Disqualified'}
            </Typography>
          </Stack>

          <Stack spacing="16px">
            <Typography
              variant={mdUp ? 'subheading2' : 'subheading1'}
              fontSize={{
                xs: '16px',
                md: '20px',
              }}
            >
              Qualifying Criteria
            </Typography>
            <Stack
              className={
                skillMatch?.selected
                  ? classes.screeningItemDiv
                  : classes.screeningItemDivError
              }
              spacing="20px"
              padding={{
                xs: '16px 16px',
                md: '16px 20px',
              }}
            >
              <QualifiedTag
                success={skillMatch?.selected}
                forCriteria={false}
              />
              <Stack
                spacing={{
                  xs: '8px',
                  md: '8px',
                }}
              >
                <Typography
                  varaint={mdUp ? 'subheading1' : 'subheading2'}
                  fontSize={{
                    md: '18px',
                    xs: '16px',
                  }}
                  fontWeight={600}
                >
                  Primary Skill
                </Typography>
                {skillMatch?.primary_skill && (
                  <Typography
                    variant={mdUp ? 'paragraph' : 'caption'}
                    sx={{ fontWeight: 500, color: 'content.primaryBlack2' }}
                  >
                    {skillMatch?.primary_skill}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={{ md: '8px', xs: '8px' }}>
                <Typography
                  varaint={mdUp ? 'subheading1' : 'subheading2'}
                  fontSize={{
                    md: '18px',
                    xs: '16px',
                  }}
                  fontWeight={600}
                >
                  Remarks
                </Typography>
                <Typography
                  variant={mdUp ? 'paragraph' : 'caption'}
                  fontWeight={500}
                  className={classes.remarkText}
                >
                  {skillMatch?.match_remark}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {skillMatch?.selected === true && criteriaMatch !== null && (
            <Stack spacing="16px">
              <Typography
                variant={mdUp ? 'subheading2' : 'subheading1'}
                fontSize={{
                  xs: '16px',
                  md: '20px',
                }}
              >
                Preferred Criteria
              </Typography>
              {Object.values(criteriaMatch?.criteria_wise_scores)?.map(
                (item, index) => (
                  <Stack
                    key={index}
                    className={
                      item?.score > 5.0
                        ? classes.screeningItemDiv
                        : classes.screeningItemDivError
                    }
                    spacing="20px"
                    sx={{
                      spacing: {
                        md: '20px',
                        xs: '24px',
                      },
                      padding: {
                        xs: '16px',
                        md: '20px',
                      },
                    }}
                  >
                    <QualifiedTag success={item?.score > 5.0} forCriteria />
                    <Stack spacing={{ md: '8px', xs: '12px' }}>
                      <Typography
                        varaint={mdUp ? 'subheading1' : 'subheading2'}
                        fontSize={{
                          md: '18px',
                          xs: '16px',
                        }}
                        fontWeight={600}
                      >
                        Criteria {index + 1}
                      </Typography>
                      {item?.name && (
                        <Typography
                          variant={mdUp ? 'paragraph' : 'caption'}
                          sx={{
                            fontWeight: 500,
                            color: 'content.primaryBlack2',
                          }}
                        >
                          {item?.name}
                        </Typography>
                      )}
                    </Stack>
                    <Stack spacing={{ md: '8px', xs: '12px' }}>
                      <Typography
                        varaint={mdUp ? 'subheading1' : 'subheading2'}
                        fontSize={{
                          md: '18px',
                          xs: '16px',
                        }}
                        fontWeight={600}
                      >
                        Remarks
                      </Typography>
                      <Typography
                        variant={mdUp ? 'paragraph' : 'caption'}
                        fontWeight={500}
                        className={classes.remarkText}
                      >
                        {item?.remark}
                      </Typography>
                    </Stack>
                  </Stack>
                ),
              )}
            </Stack>
          )}
        </>
      ) : (
        <FallbackUI content="No data found" />
      )}
    </Stack>
  );
};

export default ScreeningGroup;

ScreeningGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};

QualifiedTag.propTypes = {
  success: PropTypes.bool.isRequired,
  forCriteria: PropTypes.bool.isRequired,
};
